import React, { useId } from 'react';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { GLYPHS, LOGO_VIEWBOX } from '../brand/logoPaths';
import { colors, motion as m } from '../brand/tokens';

const DOT = GLYPHS.find((g) => g.id === 'iDot');
const DOT_CX = (DOT.bounds[0] + DOT.bounds[2]) / 2;
const DOT_CY = (DOT.bounds[1] + DOT.bounds[3]) / 2;
const DOT_R = (DOT.bounds[2] - DOT.bounds[0]) / 2;
const G_BOUNDS = GLYPHS[0].bounds;

// Per-animation settings for each glyph. `i` is the glyph index, `dot` whether it's the tittle.
const ANIMATIONS = {
  rise: (i, dot) =>
    dot
      ? { initial: { y: -90, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { ...m.spring.bouncy, delay: 0.45 } }
      : { initial: { y: 36, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { ...m.spring.snappy, delay: i * 0.06 } },
  draw: (i) => ({
    initial: { pathLength: 0, fillOpacity: 0 },
    animate: { pathLength: 1, fillOpacity: 1 },
    transition: {
      pathLength: { duration: 1, ease: 'easeInOut', delay: i * 0.08 },
      fillOpacity: { duration: 0.4, delay: 0.9 + i * 0.08 },
    },
  }),
  bounce: (i, dot) =>
    dot
      ? {
          animate: { y: [0, -28, 0] },
          transition: { duration: 0.9, repeat: Infinity, ease: [0.33, 0, 0.67, 1], repeatDelay: 0.15 },
        }
      : {},
  wave: (i) => ({
    animate: { y: [0, -10, 0] },
    transition: { duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.12 },
  }),
};

function safeId(id) {
  return id.replace(/[^a-zA-Z0-9_-]/g, '');
}

/**
 * The Groni wordmark as inline SVG.
 *
 * variant:   'wordmark' | 'monogram'
 * tittle:    'dot' (current logo) | 'slice' (revised orange-slice dot)
 * animation: 'none' | 'rise' | 'draw' | 'pour' | 'bounce' | 'wave'
 */
export function Logo({
  width = 160,
  color = colors.orange,
  variant = 'wordmark',
  tittle = 'dot',
  animation = 'none',
  title = 'groni',
  sx,
  ...rest
}) {
  const reduceMotion = useReducedMotion();
  const uid = safeId(useId());
  const anim = reduceMotion ? 'none' : animation;

  const isMonogram = variant === 'monogram';
  const pad = 6;
  const viewBox = isMonogram
    ? `${G_BOUNDS[0] - pad} ${G_BOUNDS[1] - pad} ${G_BOUNDS[2] - G_BOUNDS[0] + pad * 2} ${G_BOUNDS[3] - G_BOUNDS[1] + pad * 2}`
    : `0 0 ${LOGO_VIEWBOX.width} ${LOGO_VIEWBOX.height}`;

  const glyphs = isMonogram ? [GLYPHS[0]] : GLYPHS;
  const stroke = anim === 'draw' ? { stroke: color, strokeWidth: 2 } : {};

  const renderGlyph = (glyph, i) => {
    const isDot = glyph.id === 'iDot';
    const props = ANIMATIONS[anim]?.(i, isDot) ?? {};

    if (isDot && tittle === 'slice') {
      return (
        <motion.g key={glyph.id} {...props}>
          <circle cx={DOT_CX} cy={DOT_CY} r={DOT_R + 1.5} fill={color} mask={`url(#${uid}-slice)`} />
        </motion.g>
      );
    }
    return <motion.path key={glyph.id} d={glyph.d} {...stroke} {...props} />;
  };

  return (
    <Box
      component="svg"
      viewBox={viewBox}
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
      sx={{ width, height: 'auto', display: 'block', overflow: 'visible', ...sx }}
      {...rest}
    >
      <defs>
        {tittle === 'slice' && (
          <mask id={`${uid}-slice`}>
            <circle cx={DOT_CX} cy={DOT_CY} r={DOT_R + 1.5} fill="#fff" />
            <circle cx={DOT_CX} cy={DOT_CY} r={DOT_R - 3} fill="none" stroke="#000" strokeWidth="2" />
            {[0, 60, 120].map((deg) => (
              <line
                key={deg}
                x1={DOT_CX}
                y1={DOT_CY - DOT_R + 5}
                x2={DOT_CX}
                y2={DOT_CY + DOT_R - 5}
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                transform={`rotate(${deg} ${DOT_CX} ${DOT_CY})`}
              />
            ))}
          </mask>
        )}
        {anim === 'pour' && (
          <clipPath id={`${uid}-pour`}>
            <motion.path
              initial={{ y: LOGO_VIEWBOX.height + 20 }}
              animate={{ y: -20 }}
              transition={{ duration: m.duration.pour * 1.5, ease: m.ease.standard }}
              d={`M -20 10 Q 60 -6 122 10 T 245 10 T 367 10 T 510 10 V ${LOGO_VIEWBOX.height + 60} H -20 Z`}
            />
          </clipPath>
        )}
      </defs>
      {anim === 'pour' && (
        <g fill={color} fillOpacity={0.18} fillRule="evenodd">
          {glyphs.map((g) => (g.id === 'iDot' && tittle === 'slice' ? null : <path key={g.id} d={g.d} />))}
        </g>
      )}
      <g fill={color} fillRule="evenodd" clipPath={anim === 'pour' ? `url(#${uid}-pour)` : undefined}>
        {glyphs.map(renderGlyph)}
      </g>
    </Box>
  );
}

export default Logo;
